import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { Props as PlayerDrawerProps } from 'components/player/PlayerDrawer';
import { PlayerDrawer } from 'components/player/PlayerDrawer';
import {
  PlayerMarketDrawer,
  PlayerMarketDrawerVariant,
} from 'components/player/PlayerMarketDrawer';

import { BestPerformers } from '../BestPerformers';
import {
  BestPredictions,
  PREDICTIONS_SORTS_BY_SPORT,
} from '../BestPredictions';
import { Filters } from '../Filters';
import { FriendsRecentPurchases } from '../FriendsRecentPurchases';
import { PopularWatchlists } from '../PopularWatchlists';
import { TrendingAuctions } from '../TrendingAuctions';
import type {
  MarketHomePageQuery,
  MarketHomePageQueryVariables,
} from './__generated__/index.graphql';

const Root = styled.div`
  position: relative;
  isolation: isolate;
`;

const Layout = styled(Horizontal)`
  gap: 0;
`;
const Main = styled(Vertical)`
  max-width: 100%;
  padding: var(--double-unit) 0 var(--quadruple-unit);
  @media ${laptopAndAbove} {
    padding: var(--double-unit) 0 calc(var(--unit) * 6);
  }
`;

const MARKET_HOME_PAGE_QUERY = gql`
  query MarketHomePageQuery(
    $sport: Sport!
    $so5CompetitionSlug: String!
    $predictionsFilters: String!
    $predictionsSorts: [SearchSortInput!]
    $rarity: Rarity!
  ) {
    market {
      id
      publicWatchlists(
        sport: $sport
        first: 20
        sortBy: MOST_FOLLOWED_WITHIN_SEVEN_DAYS
        so5CompetitionSlug: $so5CompetitionSlug
      ) {
        nodes {
          slug
          ...PopularWatchlists_watchlist
        }
      }
    }
    forYou {
      friendsRecentPurchases(
        so5CompetitionSlug: $so5CompetitionSlug
        sport: $sport
        rarity: $rarity
      ) {
        id
        ...FriendsRecentPurchases_tokenOwner
      }
    }
    bestPredictions: searchPlayers(
      query: ""
      page: 1
      pageSize: 30
      advancedFilters: $predictionsFilters
      sorts: $predictionsSorts
    ) {
      ...BestPredictions_searchPlayers
    }
    so5 {
      so5Fixture(type: UPCOMING, sport: $sport) {
        slug
        ...BestPredictions_so5Fixture
      }
    }
  }
  ${PopularWatchlists.fragments.watchlist}
  ${FriendsRecentPurchases.fragments.tokenOwner}
  ${BestPredictions.fragments.searchPlayers}
  ${BestPredictions.fragments.so5Fixture}
` as TypedDocumentNode<MarketHomePageQuery, MarketHomePageQueryVariables>;

export const MarketHomePage = ({ sport }: { sport: Sport }) => {
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);
  const [marketPlayerSlug, setMarketPlayerSlug] = useState<string | null>(null);
  const isLaptopAndAbove = useIsLaptop();
  const { lifecycle } = useLifecycle();
  const rarity =
    lifecycle?.marketHomeFilters?.[sport]?.rarity || Rarity.limited;
  const so5CompetitionSlug =
    lifecycle?.marketHomeFilters?.[sport]?.competitionSlug || '';

  const { scoutableSo5Competitions } = useSo5Competitions();
  const { algoliaFilters } = scoutableSo5Competitions.find(
    competition => competition.slug === so5CompetitionSlug
  ) || { algoliaFilters: `sport:${sport.toLowerCase()}` };

  const { data, loading } = useQuery(MARKET_HOME_PAGE_QUERY, {
    variables: {
      sport,
      so5CompetitionSlug:
        so5CompetitionSlug !== 'DEFAULT' ? so5CompetitionSlug : '',
      predictionsFilters: joinFiltersWithAnd([algoliaFilters]),
      predictionsSorts: PREDICTIONS_SORTS_BY_SPORT[sport],
      rarity,
    },
  });

  const { publicWatchlists } = data?.market || {};
  const { so5Fixture } = data?.so5 || {};

  return (
    <Root>
      <Filters sport={sport} />
      <Layout>
        <Main gap={isLaptopAndAbove ? 6 : 5}>
          <BestPredictions
            predictions={data?.bestPredictions}
            loading={loading}
            sport={sport}
            so5Fixture={so5Fixture}
            onStatsClick={slug =>
              setCardOpened({ cardSlug: '', playerSlug: slug })
            }
          />
          <TrendingAuctions sport={sport} />
          <BestPerformers
            onStatsClick={slug =>
              setCardOpened({ cardSlug: '', playerSlug: slug })
            }
          />
          <FriendsRecentPurchases
            tokenOwners={data?.forYou?.friendsRecentPurchases || []}
          />
          <PopularWatchlists watchlists={publicWatchlists?.nodes || []} />
        </Main>
      </Layout>
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
        withBuyingOptions={{
          rarity,
          inSeason: true,
          onMarketClick: slug => setMarketPlayerSlug(slug),
        }}
      />
      <PlayerMarketDrawer
        onClose={() => setMarketPlayerSlug(null)}
        playerSlug={marketPlayerSlug}
        sport={sport}
        canUseConversionCredit
        origin={{
          instantBuy: ClickInstantBuy_Flow.MARKETPLACE,
          addToCart: ClickAddToCart_Origin.MARKETPLACE,
        }}
        variant={PlayerMarketDrawerVariant.SCOUTING_CENTER}
      />
    </Root>
  );
};
